import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { eur, num, formatApiError } from "@/lib/api";
import PointsMap from "@/components/PointsMap";
import { normalizeProvince, provinceLabel } from "@/lib/provinces";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ReferentiEditor from "@/components/ReferentiEditor";
import ProspectFormDialog from "@/components/ProspectFormDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Filter, Target, TrendingUp, ArrowRightCircle, Loader2, Building2, Save, Clock, MapPin, Users, ArchiveRestore, Download, Skull, Info, Wrench, Mail, Sparkles } from "lucide-react";
import ProspectFunnelConfigPanel from "@/components/ProspectFunnelConfigPanel";
import PresidioDialog from "@/components/PresidioDialog";
import QuickAdd from "@/components/QuickAdd";
import VisitaB2BDialog from "@/components/VisitaB2BDialog";
import { ClipboardCheck } from "lucide-react";
import ActivityLog from "@/components/ActivityLog";
import { QuotesForClient } from "@/components/QuotesForClient";
import FlagPicker, { SETTORI, FONTI } from "@/components/SettoriPicker";

const STAGES = ["nuovo", "contattato", "qualificato", "trattativa", "vinto", "ordine", "perso"];
const STAGE_LABEL = { nuovo: "Non contattato", contattato: "Primo contatto", qualificato: "Parlato col decision maker", trattativa: "Incontro presso cliente/azienda", vinto: "Partner", ordine: "Ordine", perso: "Perso" };
const AUTO_STAGES = ["vinto", "ordine"];
// Palette colori per agenzia (mappa prospect): un colore distinto per ciascuna agenzia.
const AGENCY_PALETTE = ["#2563eb", "#16a34a", "#f59e0b", "#d946ef", "#ef4444", "#06b6d4", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#84cc16", "#0ea5e9", "#a855f7", "#eab308", "#22c55e", "#e11d48", "#3b82f6", "#10b981"];
const DIREZIONALE_COLOR = "#64748b";
const ASSIGN_LABEL = { file: "Da colonna file", territorio: "Assegnato per territorio", manuale: "Assegnato manualmente", direzionale: "Direzionale (no agenzia)" };
const STAGE_STYLE = {
  nuovo: "border-slate-500/40 bg-slate-500/15 text-slate-300", contattato: "border-sky-500/40 bg-sky-500/15 text-sky-300",
  qualificato: "border-blue-500/40 bg-blue-500/15 text-blue-300", trattativa: "border-amber-500/40 bg-amber-500/15 text-amber-300",
  vinto: "border-green-500/40 bg-green-500/15 text-green-300", ordine: "border-fuchsia-500/40 bg-fuchsia-500/15 text-fuchsia-300", perso: "border-red-500/40 bg-red-500/15 text-red-300",
};
const dueInfo = (d) => {
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(d + "T00:00:00");
  if (isNaN(due.getTime())) return null;
  const days = Math.round((due - today) / 86400000);
  if (days < 0) return { label: `Scaduta ${Math.abs(days)}g`, cls: "border-red-500/50 bg-red-500/20 text-red-300" };
  if (days === 0) return { label: "Oggi", cls: "border-amber-500/60 bg-amber-500/25 text-amber-200" };
  if (days <= 3) return { label: `Tra ${days}g`, cls: "border-amber-500/30 bg-amber-500/10 text-amber-200" };
  return { label: due.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }), cls: "border-white/15 bg-white/5 text-muted-foreground" };
};
const EMPTY = { name: "", referente: "", email: "", phone: "", region: "", province: "", city: "", address: "", cap: "", partita_iva: "", codice_fiscale: "", forma_giuridica: "", data_costituzione: "", capitale_sociale: "", codice_sdi: "", ateco: "", website: "", source: "", stage: "nuovo", agency_id: "none", potenziale_annuo: "", expected_value: "", next_action: "", next_action_date: "", notes: "", lost_reason: "", categoria: "", tipo_cliente: "azienda", settore: "", fonte_contatto: "", note_inseritore: "", affidamento_max: "", affidamento_note: "", referenti: [] };

function EsitoBackfillPanel() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const poll = () => api.get("/prospects/esito-backfill/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { poll(); const t = setInterval(poll, 3000); return () => clearInterval(t); }, []);
  const start = async () => { setBusy(true); try { await api.post("/prospects/esito-backfill"); await poll(); } finally { setBusy(false); } };
  const running = st?.running;
  const pct = st && st.total ? Math.round((st.done / st.total) * 100) : 0;
  return (
    <Card className="border-primary/40 bg-primary/[0.05] p-4" data-testid="esito-backfill-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading text-base font-bold">Classifica esiti azioni prospect (AI)</div>
          <p className="text-xs text-muted-foreground">Analizza le note delle azioni e deduce Raggiungimento, Accesso Decision Maker e Generazione visita. Operazione una-tantum su tutto lo storico; poi i nuovi inserimenti vengono classificati in automatico.</p>
        </div>
        <Button onClick={start} disabled={busy || running} data-testid="esito-backfill-btn" className="shrink-0">
          {running ? "Classificazione in corso…" : "Avvia classificazione AI"}
        </Button>
      </div>
      {st && (st.running || st.done > 0) && (
        <div className="mt-3" data-testid="esito-backfill-progress">
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
          <div className="mt-1 text-xs text-muted-foreground">{st.done}/{st.total} analizzate · {st.updated} classificate {running ? `· ${pct}%` : "· completato"}</div>
        </div>
      )}
    </Card>
  );
}

export default function Prospects({ mode }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isBackOffice = user?.role === "back_office" || user?.role_actual === "back_office";
  const canManage = user?.role === "admin" || user?.role === "commercial_manager" || isBackOffice;
  const canAssign = canManage || user?.role === "head_of_partner";
  const canEdit = canManage || user?.role === "agency";
  const riskCls = (r) => {
    const s = (r || "").toUpperCase();
    if (s.includes("LOW")) return "border-green-500/40 bg-green-500/15 text-green-400";
    if (s.includes("MODERATE") || s.includes("MEDIO")) return "border-amber-500/40 bg-amber-500/15 text-amber-400";
    if (s.includes("HIGH") || s.includes("ALTO")) return "border-red-500/40 bg-red-500/15 text-red-400";
    return "border-slate-500/40 bg-slate-500/15 text-slate-300";
  };
  const [items, setItems] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [agencyFilter, setAgencyFilter] = useState("all");
  const [dialog, setDialog] = useState(false);
  const [pvAtt, setPvAtt] = useState(false);
  const [pvVisita, setPvVisita] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [savingAffido, setSavingAffido] = useState(false);
  const savePreAffido = async () => {
    if (!editing) return;
    setSavingAffido(true);
    try {
      await api.put(`/prospects/${editing.id}/affidamento`, { affidamento_max: form.affidamento_max === "" ? null : form.affidamento_max, note: form.affidamento_note });
      toast.success("Pre-affido salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingAffido(false); }
  };
  const [convBusy, setConvBusy] = useState(false);
  const convertiCliente = async () => {
    if (!editing) return;
    if (!window.confirm("Convertire questo prospect in Cliente? Verranno trasferiti fido preliminare e bilancio nella nuova anagrafica.")) return;
    setConvBusy(true);
    try {
      const r = await api.post(`/prospects/${editing.id}/convert-cliente`);
      toast.success("Prospect convertito in Cliente");
      window.open(`/partners/${r.data.partner_id}`, "_blank");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setConvBusy(false); }
  };
  const [view, setView] = useState("kanban");
  const RENDER_CAP = 200;
  const [search, setSearch] = useState("");
  const dragId = useRef(null);
  const updateStage = async (p, stage) => {
    if (!canEdit || p.stage === stage) return;
    if (AUTO_STAGES.includes(stage)) { toast.info("Colonna automatica: si popola da sola (conversione a Partner / primo ordine)"); return; }
    if (AUTO_STAGES.includes(p.stage)) { toast.info("Questo prospect è già Partner: lo stage è automatico"); return; }
    // Gli stadi del funnel sono automatici (derivati dalle attività). A mano si può solo marcare/togliere 'Perso'.
    if (stage !== "perso" && p.stage !== "perso") {
      toast.info("Gli stadi si aggiornano automaticamente dalle attività registrate. Manualmente puoi solo marcare 'Perso'.");
      return;
    }
    try { await api.put(`/prospects/${p.id}`, { ...p, stage, agency_id: p.agency_id || null }); toast.success(`${p.name} → ${STAGE_LABEL[stage]}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [objDraft, setObjDraft] = useState({});
  const [savingObj, setSavingObj] = useState(null);
  const [geo, setGeo] = useState(null);
  const [assignSummary, setAssignSummary] = useState(null);
  const [reassignPreview, setReassignPreview] = useState(null);
  const [reassigning, setReassigning] = useState(false);
  const [fixingInv, setFixingInv] = useState(false);
  const fixInvalidAgency = async () => {
    setFixingInv(true);
    try {
      const dr = await api.post("/prospects/fix-invalid-agency", { dry_run: true });
      const { invalid_total, ricollegati, per_territorio, a_direzionale } = dr.data;
      if (!invalid_total) { toast.info("Nessuna assegnazione non valida da correggere."); return; }
      if (!window.confirm(`${invalid_total} prospect con agenzia inesistente:\n• ${ricollegati} ricollegati all'agenzia corretta\n• ${per_territorio} riassegnati per territorio\n• ${a_direzionale} messi direzionali (nessun territorio)\nProcedere?`)) return;
      const r = await api.post("/prospects/fix-invalid-agency", { dry_run: false });
      toast.success(`Corrette ${r.data.applied} assegnazioni`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFixingInv(false); }
  };
  const [terrOverrides, setTerrOverrides] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAgency, setBulkAgency] = useState("");
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const toggleSelect = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSelection = () => setSelectedIds(new Set());
  const bulkAssign = async () => {
    if (!bulkAgency || selectedIds.size === 0) return;
    const target = agencies.find((a) => a.id === bulkAgency);
    const agency_id = bulkAgency === "none" ? null : bulkAgency;
    const label = bulkAgency === "none" ? "Direzionale (nessuna agenzia)" : target?.name;
    if (!window.confirm(`Riassegnare ${selectedIds.size} prospect a "${label}"?`)) return;
    setBulkAssigning(true);
    try {
      const r = await api.post("/prospects/bulk-assign", { ids: [...selectedIds], agency_id });
      toast.success(`Riassegnati ${r.data.applied} prospect`);
      clearSelection(); setBulkAgency(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBulkAssigning(false); }
  };
  const loadAssignSummary = () => api.get("/prospects/assignment-summary").then((r) => setAssignSummary(r.data)).catch(() => {});
  const previewReassign = async () => {
    try { const r = await api.post("/prospects/reassign-territory", { dry_run: true }); setReassignPreview(r.data); setTerrOverrides({}); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirmReassign = async () => {
    setReassigning(true);
    try {
      const r = await api.post("/prospects/reassign-territory", { dry_run: false, overrides: terrOverrides });
      toast.success(`Riassegnati ${r.data.applied} prospect`);
      setReassignPreview(null); setTerrOverrides({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReassigning(false); }
  };
  const loadGeo = () => api.get("/prospects/geocode-status").then((r) => setGeo(r.data)).catch(() => {});
  const startGeo = async () => {
    try {
      const r = await api.post("/prospects/geocode");
      const n = r.data?.retry || 0;
      if (n > 0) toast.success(`Geolocalizzazione avviata · ${n} indirizzi da riprovare`);
      else if (geo?.completabili > 0) toast.success("Geolocalizzazione avviata");
      else toast.info("Tutti gli indirizzi risolvibili sono già geolocalizzati. I restanti non hanno dati sufficienti (manca città/provincia): scarica il CSV per completarli.");
      setTimeout(loadGeo, 800);
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const downloadGeoMissing = async () => {
    try {
      const res = await api.get("/prospects/geo-missing.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "prospect_non_geolocalizzati.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { loadGeo(); const t = setInterval(loadGeo, 5000); return () => clearInterval(t); }, []);

  const [showArchived, setShowArchived] = useState(false);
  const [presidioView, setPresidioView] = useState("attivo");
  const [presidioCounts, setPresidioCounts] = useState({ panchina: 0, cimitero: 0 });
  const [funnelLabels, setFunnelLabels] = useState({});
  const [presidioDlg, setPresidioDlg] = useState({ open: false, target: null, mode: "move" });
  const restore = async (p) => {
    try { await api.post(`/prospects/${p.id}/restore`); toast.success("Prospect ripristinato"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const load = async () => {
    setLoading(true);
    const [p, f, a, r] = await Promise.all([
      api.get("/prospects", { params: { ...(showArchived ? { include_archived: true } : {}), presidio: presidioView } }), api.get("/prospects/funnel"),
      api.get("/agencies").catch(() => ({ data: [] })), api.get("/regions"),
    ]);
    setItems(p.data); setFunnel(f.data); setAgencies(a.data); setRegions(r.data);
    api.get("/prospects/presidio-counts").then((r) => setPresidioCounts(r.data)).catch(() => {});
    api.get("/prospect-funnel-config").then((r) => { const m = {}; (r.data.order || []).forEach((fid) => { m[fid] = r.data.funnels[fid]?.nome || fid; }); setFunnelLabels(m); }).catch(() => {});
    if (canManage) loadAssignSummary();
    if (canManage) {
      const ov = await api.get("/prospects/objectives-overview").then((x) => x.data).catch(() => null);
      if (ov) {
        setOverview(ov);
        const draft = { global: { ...ov.global.objective } };
        ov.agencies.forEach((row) => { draft[row.agency_id] = { ...row.objective }; });
        setObjDraft(draft);
      }
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [showArchived, presidioView]);

  const [mapPoints, setMapPoints] = useState([]);
  const [mapGeo, setMapGeo] = useState([]);
  const [regionFilter, setRegionFilter] = useState("all");
  const [provinceFilter, setProvinceFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [mapSel, setMapSel] = useState(null);
  const STAGE_COLOR = { nuovo: "#94a3b8", contattato: "#38bdf8", qualificato: "#2563eb", trattativa: "#f59e0b", vinto: "#16a34a", ordine: "#d946ef", perso: "#ef4444" };
  const [showMap, setShowMap] = useState(true);
  const [mapCF, setMapCF] = useState("escludi");
  const [mapLoaded, setMapLoaded] = useState(false);
  useEffect(() => {
    if (showMap && !mapLoaded) {
      api.get("/prospects/map").then((r) => { setMapGeo(r.data || []); setMapLoaded(true); }).catch(() => {});
    }
  }, [showMap, mapLoaded]);

  const setObj = (key, field) => (e) => {
    const v = e.target.value;
    setObjDraft((d) => ({ ...d, [key]: { ...d[key], [field]: v } }));
  };

  const saveObjective = async (key, agencyId) => {
    setSavingObj(key);
    const d = objDraft[key] || {};
    try {
      await api.put("/prospect-objectives", {
        month: overview.month, agency_id: agencyId,
        target_new: parseInt(d.target_new) || 0,
        target_qualified: parseInt(d.target_qualified) || 0,
        target_won: parseInt(d.target_won) || 0,
      });
      toast.success("Obiettivo salvato");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingObj(null); }
  };


  const matchAg = (p) => agencyFilter === "all" || (agencyFilter === "none" ? !p.agency_id : p.agency_id === agencyFilter);
  const matchRegProv = (p) => (regionFilter === "all" || p.region === regionFilter) && (provinceFilter === "all" || normalizeProvince(p.province) === provinceFilter);
  const matchSource = (p) => sourceFilter === "all" || (p.source || "").trim() === sourceFilter;
  const sourceOptions = useMemo(() => [...new Set(items.map((p) => (p.source || "").trim()).filter(Boolean))].sort(), [items]);
  const provinceOptions = useMemo(() => {
    const m = new Map();
    items.filter((p) => regionFilter === "all" || p.region === regionFilter).forEach((p) => {
      const key = normalizeProvince(p.province);
      if (key && !m.has(key)) m.set(key, provinceLabel(key));
    });
    return [...m.entries()].map(([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [items, regionFilter]);
  const filtered = useMemo(() => items.filter((p) => (filter === "all" || p.stage === filter) && matchRegProv(p) && matchAg(p) && matchSource(p) && (!mapSel || mapSel.has(p.id))), [items, filter, regionFilter, provinceFilter, agencyFilter, sourceFilter, mapSel]);
  const tableRows = useMemo(() => filtered.filter((p) => !search || (p.name || "").toLowerCase().includes(search.toLowerCase())).slice(0, RENDER_CAP), [filtered, search]);
  const allVisibleSelected = tableRows.length > 0 && tableRows.every((p) => selectedIds.has(p.id));
  const toggleSelectAll = () => setSelectedIds((s) => {
    const n = new Set(s);
    if (allVisibleSelected) tableRows.forEach((p) => n.delete(p.id));
    else tableRows.forEach((p) => n.add(p.id));
    return n;
  });
  const coordById = useMemo(() => Object.fromEntries(mapGeo.map((c) => [c.id, c])), [mapGeo]);
  const agencyColorById = useMemo(() => { const m = {}; agencies.forEach((a, i) => { m[a.id] = AGENCY_PALETTE[i % AGENCY_PALETTE.length]; }); return m; }, [agencies]);
  const agencyNameById = useMemo(() => Object.fromEntries(agencies.map((a) => [a.id, a.name])), [agencies]);
  const inMap = (p) => mapCF === "solo" ? p.categoria === "cliente_finale" : p.categoria !== "cliente_finale";
  const mapPoints2 = useMemo(() => filtered.filter(inMap).map((p) => {
    const c = coordById[p.id];
    if (!c) return null;
    const col = p.agency_id ? (agencyColorById[p.agency_id] || DIREZIONALE_COLOR) : DIREZIONALE_COLOR;
    return {
      id: p.id, lat: c.lat, lng: c.lng,
      color: col,
      borderWeight: 1,
      label: p.name,
      tooltip: `${p.name}${p.city ? " · " + p.city : ""} · ${p.agency_id ? (agencyNameById[p.agency_id] || "Agenzia") : "Direzionale"}`,
    };
  }).filter(Boolean), [filtered, coordById, mapCF, agencyColorById, agencyNameById]);
  const legendAgencies = useMemo(() => {
    const present = new Set(); let direzionali = false;
    filtered.forEach((p) => { if (!coordById[p.id] || !inMap(p)) return; if (p.agency_id && agencyColorById[p.agency_id]) present.add(p.agency_id); else direzionali = true; });
    return { list: agencies.filter((a) => present.has(a.id)), direzionali };
  }, [filtered, coordById, mapCF, agencyColorById, agencies]);

  const boardItems = useMemo(() => items.filter((p) => !p.archived && matchRegProv(p) && matchAg(p)), [items, regionFilter, provinceFilter, agencyFilter]);
  const byStage = useMemo(() => {
    const m = {}; STAGES.forEach((s) => (m[s] = []));
    boardItems.forEach((p) => { (m[p.stage] || (m[p.stage] = [])).push(p); });
    return m;
  }, [boardItems]);

  const setV = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const navigate = useNavigate();
  const [formDlg, setFormDlg] = useState(false);
  const openNew = () => setFormDlg(true);
  const openEdit = (p) => navigate(`/prospect/${p.id}`);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && items.length) {
      const p = items.find((x) => x.id === openId);
      if (p) { openEdit(p); setSearchParams({}, { replace: true }); }
    }
  }, [items, searchParams]);

  const save = async () => {
    setSaving(true);
    const payload = {
      name: form.name, referente: form.referente, email: form.email, phone: form.phone,
      region: form.region, city: form.city, source: form.source, stage: form.stage,
      agency_id: form.agency_id === "none" ? null : form.agency_id,
      potenziale_annuo: form.potenziale_annuo === "" ? null : parseFloat(form.potenziale_annuo),
      expected_value: form.expected_value === "" ? null : parseFloat(form.expected_value),
      next_action: form.next_action, next_action_date: form.next_action_date || null,
      notes: form.notes, lost_reason: form.lost_reason,
      categoria: form.categoria || null, tipo_cliente: form.categoria === "cliente_finale" ? form.tipo_cliente : null,
      settore: form.categoria === "cliente_finale" && form.tipo_cliente === "azienda" ? form.settore : null,
      fonte_contatto: form.categoria === "cliente_finale" ? form.fonte_contatto : null,
      note_inseritore: form.categoria === "cliente_finale" ? form.note_inseritore : null,
      province: form.province || null, address: form.address || null, cap: form.cap || null,
      partita_iva: form.partita_iva || null, codice_fiscale: form.codice_fiscale || null,
      forma_giuridica: form.forma_giuridica || null, ateco: form.ateco || null,
      data_costituzione: form.data_costituzione || null, codice_sdi: form.codice_sdi || null,
      website: form.website || null,
      capitale_sociale: (form.capitale_sociale === "" || form.capitale_sociale == null) ? null : parseFloat(form.capitale_sociale),
      referenti: form.referenti || [],
    };
    try {
      if (editing) await api.put(`/prospects/${editing.id}`, payload);
      else await api.post("/prospects", payload);
      toast.success(editing ? "Prospect aggiornato" : "Prospect creato");
      setDialog(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"?`)) return;
    await api.delete(`/prospects/${p.id}`); toast.success("Eliminato"); load();
  };

  const convert = async (p) => {
    if (!window.confirm(`Convertire "${p.name}" in Partner?`)) return;
    try { await api.post(`/prospects/${p.id}/convert`); toast.success("Convertito in Partner"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  if (loading || !funnel)
    return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const maxCount = Math.max(1, ...funnel.stages.map((s) => s.count));
  const obj = funnel.objective, prog = funnel.progress;
  const pct = (v, t) => t > 0 ? Math.min(100, Math.round((v / t) * 100)) : 0;

  return (
    <div className="flex flex-col gap-6" data-testid="prospects-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Prospect</h1>
          <p className="text-sm text-muted-foreground">Funnel di acquisizione nuovi partner e obiettivi di azioni su prospect. Gli stadi si aggiornano automaticamente dalle attività registrate.</p>
        </div>
        {canEdit && <Button onClick={openNew} data-testid="add-prospect-btn"><Plus className="mr-2 h-4 w-4" /> Nuovo Prospect</Button>}
      </div>

      {mode === "funnel" && <div className="space-y-4"><EsitoBackfillPanel /><ProspectFunnelConfigPanel /></div>}

      {mode !== "funnel" && (
      <Card className="order-1 rounded-b-none border-b-0 p-4" data-testid="prospects-map-card">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-lg font-bold tracking-tight">Geolocalizzazione prospect</h2>
          {isAdmin && geo && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="prospect-geocode-status">
              <div className="text-xs text-muted-foreground">{geo.geocoded} / {geo.total} geolocalizzati{geo.running ? " · in corso…" : ""}{geo.senza_posizione ? ` · ${geo.senza_posizione} senza posizione${geo.completabili ? ` (${geo.completabili} completabili)` : ""}` : ""}</div>
              <div className="h-1.5 w-32 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${geo.total ? Math.round((geo.geocoded / geo.total) * 100) : 0}%` }} />
              </div>
              <Button variant="outline" size="sm" onClick={startGeo} disabled={geo.running} data-testid="prospect-geocode-btn">
                {geo.running ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> In corso…</> : <><MapPin className="mr-2 h-4 w-4" /> Geolocalizza indirizzi</>}
              </Button>
              <Button variant="ghost" size="sm" onClick={downloadGeoMissing} data-testid="prospect-geo-missing-btn" title="Scarica il CSV dei prospect non geolocalizzati con il motivo">
                <Download className="mr-2 h-4 w-4" /> Non geolocalizzati (CSV)
              </Button>
            </div>
          )}
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Select value={regionFilter} onValueChange={(v) => { setRegionFilter(v); setProvinceFilter("all"); }}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="prospect-region-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le regioni</SelectItem>
              {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={provinceFilter} onValueChange={setProvinceFilter}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="prospect-province-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le province</SelectItem>
              {provinceOptions.map((pr) => <SelectItem key={pr.key} value={pr.key}>{pr.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {user?.role === "admin" && (
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="h-9 w-[200px]" data-testid="prospect-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le fonti</SelectItem>
              {sourceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          )}
          <Select value={agencyFilter} onValueChange={setAgencyFilter}>
            <SelectTrigger className="h-9 w-[220px]" data-testid="prospect-agency-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le agenzie</SelectItem>
              <SelectItem value="none">Senza agenzia (direzionali)</SelectItem>
              {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {(regionFilter !== "all" || provinceFilter !== "all" || agencyFilter !== "all" || sourceFilter !== "all") && <Button variant="outline" size="sm" onClick={() => { setRegionFilter("all"); setProvinceFilter("all"); setAgencyFilter("all"); setSourceFilter("all"); setMapSel(null); }} data-testid="clear-prospect-filters">Azzera filtri ✕</Button>}
          {mapSel && <Button variant="outline" size="sm" onClick={() => setMapSel(null)} data-testid="clear-map-selection">Zona mappa: {mapSel.size} ✕</Button>}
          {filter !== "all" && <Button variant="outline" size="sm" onClick={() => setFilter("all")} data-testid="clear-stage-filter">Stage: {filter} ✕</Button>}
        </div>
        {!showMap ? (
          <button
            type="button"
            onClick={() => setShowMap(true)}
            data-testid="toggle-map-btn"
            className="group flex w-full flex-col items-center gap-3 rounded-xl border-2 border-dashed border-primary/50 bg-primary/5 p-6 text-center transition-colors hover:border-primary hover:bg-primary/10 sm:flex-row sm:justify-between sm:text-left"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary transition-transform group-hover:scale-110">
                <MapPin className="h-7 w-7" />
              </div>
              <div>
                <div className="font-heading text-lg font-bold tracking-tight">Visualizza la mappa dei prospect</div>
                <div className="text-xs text-muted-foreground">La mappa viene caricata solo su richiesta per velocizzare la pagina.</div>
              </div>
            </div>
            <span className="inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-md transition-transform group-hover:translate-x-0.5">
              <MapPin className="mr-2 h-4 w-4" /> Apri mappa
            </span>
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowMap(false)} data-testid="toggle-map-btn">
              <MapPin className="mr-2 h-4 w-4" /> Chiudi mappa
            </Button>
            <Button variant={mapCF === "solo" ? "default" : "outline"} size="sm" onClick={() => setMapCF((m) => m === "solo" ? "escludi" : "solo")} data-testid="map-cliente-finale-toggle">
              {mapCF === "solo" ? "Mostra prospect normali" : "Mostra solo clienti finali"}
            </Button>
            {!mapLoaded && <span className="text-xs text-muted-foreground"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Caricamento mappa…</span>}
          </div>
        )}
        {showMap && (
        <div className="mt-3">
        <PointsMap testid="prospects-map" cluster points={mapPoints2} onClusterClick={(ids) => setMapSel(new Set(ids))} onPointClick={(id) => { const pr = items.find((x) => x.id === id); if (pr) openEdit(pr); }} legend={<>
          <span className="font-semibold text-muted-foreground">Agenzie:</span>
          {legendAgencies.list.map((a) => (
            <span key={a.id} className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: agencyColorById[a.id] }} /> {a.name}</span>
          ))}
          {legendAgencies.direzionali && (
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: DIREZIONALE_COLOR }} /> Direzionali (senza agenzia)</span>
          )}
          {legendAgencies.list.length === 0 && !legendAgencies.direzionali && <span className="text-muted-foreground">Nessun prospect geolocalizzato</span>}
        </>} />
        </div>
        )}
      </Card>
      )}

      {mode !== "map" && (<>
      <Card className="order-4 p-6" data-testid="funnel-card">
        <div className="mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Funnel</h2></div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          {funnel.stages.map((s) => (
            <button key={s.stage} onClick={() => setFilter(s.stage)} data-testid={`funnel-stage-${s.stage}`}
              className={`rounded-md border p-3 text-left transition-transform hover:-translate-y-0.5 ${filter === s.stage ? "ring-2 ring-primary" : ""} ${STAGE_STYLE[s.stage]}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wide">{STAGE_LABEL[s.stage]}</div>
              <div className="font-heading text-2xl font-black">{s.count}</div>
              <div className="text-[11px] opacity-80">{eur(s.value)}</div>
              <div className="mt-2 h-1.5 w-full rounded bg-black/10"><div className="h-1.5 rounded bg-current" style={{ width: `${(s.count / maxCount) * 100}%` }} /></div>
            </button>
          ))}
        </div>
      </Card>

      <Card className="hidden" data-testid="objectives-card">
        <div className="mb-4 flex items-center gap-2"><Target className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Obiettivi di azioni su prospect · {funnel.month}</h2></div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {[["Nuovi prospect", prog.new, obj.target_new], ["Qualificati", prog.qualified, obj.target_qualified], ["Conversioni", prog.won, obj.target_won]].map(([label, v, t]) => (
            <div key={label}>
              <div className="mb-1 flex items-center justify-between text-sm"><span className="font-semibold">{label}</span><span className="font-mono text-muted-foreground">{v}/{t}</span></div>
              <Progress value={pct(v, t)} />
            </div>
          ))}
        </div>
      </Card>

      {false && assignSummary && (
        <Card className="order-6 p-6" data-testid="assignment-summary-card">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Assegnazione prospect per agenzia</h2></div>
            <Button variant="outline" size="sm" onClick={previewReassign} data-testid="reassign-territory-btn"><MapPin className="mr-2 h-4 w-4" /> Ricalcola assegnazione per territorio</Button>
          </div>
          <div className="mb-4 flex flex-wrap gap-3 text-sm">
            <div className="rounded-md border border-border px-3 py-2"><span className="text-xs text-muted-foreground">Totale prospect</span><div className="font-heading text-lg font-bold" data-testid="assign-total">{num(assignSummary.total)}</div></div>
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2"><span className="text-xs text-muted-foreground">Direzionali (senza agenzia)</span><div className="font-heading text-lg font-bold text-amber-400" data-testid="assign-direzionali">{num(assignSummary.direzionali)}</div></div>
          </div>
          {assignSummary.by_source && assignSummary.by_source.length > 0 && (
            <div className="mb-4" data-testid="assign-by-source">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prospect per fonte</div>
              <div className="flex flex-wrap gap-2">
                {assignSummary.by_source.map((s) => (
                  <button key={s.source} type="button" onClick={() => setSourceFilter(s.source === "—" ? "all" : s.source)} className="rounded-md border border-border px-3 py-2 text-left transition-colors hover:border-primary hover:bg-primary/5" data-testid={`source-chip-${s.source}`}>
                    <div className="text-[11px] text-muted-foreground">{s.source}</div>
                    <div className="font-heading text-base font-bold">{num(s.count)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="max-h-72 overflow-y-auto rounded-md border border-border">
            <Table>
              <TableHeader><TableRow><TableHead>Agenzia</TableHead><TableHead className="text-right">Prospect assegnati</TableHead></TableRow></TableHeader>
              <TableBody>
                {assignSummary.per_agency.map((r) => (
                  <TableRow key={r.agency_id} data-testid={`assign-row-${r.agency_id}`}>
                    <TableCell className="font-medium">{r.agency_name}</TableCell>
                    <TableCell className="text-right font-mono">{num(r.count)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell className="font-semibold text-amber-400">Direzionali (no agenzia)</TableCell>
                  <TableCell className="text-right font-mono text-amber-400">{num(assignSummary.direzionali)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          {assignSummary.invalid && assignSummary.invalid.length > 0 && (
            <div className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 p-3" data-testid="assign-invalid">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold text-red-400">⚠ Assegnazioni non valide (agenzia inesistente): {num(assignSummary.invalid_total)} prospect</div>
                <Button variant="outline" size="sm" onClick={fixInvalidAgency} disabled={fixingInv} data-testid="fix-invalid-btn">{fixingInv ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Correggi assegnazioni non valide</Button>
              </div>
              <div className="space-y-1 text-xs">
                {assignSummary.invalid.map((r) => (
                  <div key={r.agency_id} className="flex justify-between gap-2"><span className="truncate text-muted-foreground">{r.guess} <span className="opacity-60">({r.agency_id})</span></span><span className="font-mono">{num(r.count)}</span></div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <Dialog open={!!reassignPreview} onOpenChange={(o) => !o && setReassignPreview(null)}>
        <DialogContent data-testid="reassign-dialog">
          <DialogHeader><DialogTitle>Ricalcola assegnazione per territorio</DialogTitle></DialogHeader>
          {reassignPreview && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">Vengono rielaborati <b>tutti</b> i prospect in base a provincia/regione. Per i territori senza agenzia associata, scegli tu a chi assegnarli (o lasciali direzionali).</p>
              <div className="flex flex-wrap gap-3">
                <div className="rounded-md border border-border px-3 py-2"><span className="text-xs text-muted-foreground">Prospect totali</span><div className="font-heading text-lg font-bold" data-testid="reassign-candidates">{num(reassignPreview.candidates)}</div></div>
                <div className="rounded-md border border-green-500/40 bg-green-500/10 px-3 py-2"><span className="text-xs text-muted-foreground">Assegnati per territorio</span><div className="font-heading text-lg font-bold text-green-400" data-testid="reassign-assignable">{num(reassignPreview.assignable)}</div></div>
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2"><span className="text-xs text-muted-foreground">Senza dato territorio (restano direzionali)</span><div className="font-heading text-lg font-bold text-amber-400" data-testid="reassign-still">{num(reassignPreview.senza_territorio)}</div></div>
              </div>
              {reassignPreview.unresolved && reassignPreview.unresolved.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-400">Territori senza agenzia — scegli a chi assegnarli ({reassignPreview.pending_unresolved} prospect)</div>
                  <div className="max-h-60 space-y-2 overflow-y-auto rounded-md border border-border p-2">
                    {reassignPreview.unresolved.map((u) => (
                      <div key={u.key} className="flex items-center justify-between gap-2" data-testid={`unresolved-${u.key}`}>
                        <div className="min-w-0">
                          <div className="truncate font-medium">{u.province || u.region || "—"}</div>
                          <div className="text-[11px] text-muted-foreground">{u.region && u.province ? u.region + " · " : ""}{num(u.count)} prospect</div>
                        </div>
                        <Select value={terrOverrides[u.key] || "direzionale"} onValueChange={(v) => setTerrOverrides((s) => ({ ...s, [u.key]: v }))}>
                          <SelectTrigger className="h-8 w-[190px]" data-testid={`unresolved-select-${u.key}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="direzionale">Mantieni direzionale</SelectItem>
                            {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {reassignPreview.per_agency.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-md border border-border">
                  <Table>
                    <TableHeader><TableRow><TableHead>Agenzia</TableHead><TableHead className="text-right">Prospect (dopo ricalcolo)</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {reassignPreview.per_agency.map((r) => (
                        <TableRow key={r.agency_id}><TableCell>{r.agency_name}</TableCell><TableCell className="text-right font-mono">{num(r.count)}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignPreview(null)} data-testid="reassign-cancel">Annulla</Button>
            <Button onClick={confirmReassign} disabled={reassigning} data-testid="reassign-confirm">
              {reassigning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MapPin className="mr-2 h-4 w-4" />} Conferma e assegna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {false && canManage && overview && (
        <Card className="p-6" data-testid="obj-manage-card">
          <div className="mb-4 flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Obiettivi per Agenzia · {overview.month}</h2></div>
          <p className="mb-4 text-xs text-muted-foreground">Imposta target di azioni su prospect distinti per agenzia oltre a quello globale (Nuovi · Qualificati · Conversioni).</p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ambito</TableHead>
                  <TableHead className="w-28 text-center">Nuovi</TableHead>
                  <TableHead className="w-28 text-center">Qualificati</TableHead>
                  <TableHead className="w-28 text-center">Conversioni</TableHead>
                  <TableHead className="text-center">Avanzamento</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[{ key: "global", name: "Globale (tutte le agenzie)", agencyId: null, progress: overview.global.progress },
                  ...overview.agencies.map((a) => ({ key: a.agency_id, name: a.agency_name, agencyId: a.agency_id, progress: a.progress }))
                ].map((row) => {
                  const d = objDraft[row.key] || {};
                  return (
                    <TableRow key={row.key} data-testid={`obj-row-${row.key}`}>
                      <TableCell className="font-semibold">{row.name}</TableCell>
                      {["target_new", "target_qualified", "target_won"].map((f) => (
                        <TableCell key={f} className="text-center">
                          <Input type="number" min="0" className="h-8 text-center" value={d[f] ?? 0} onChange={setObj(row.key, f)} data-testid={`obj-${row.key}-${f}`} />
                        </TableCell>
                      ))}
                      <TableCell className="text-center font-mono text-xs text-muted-foreground">{row.progress.new}·{row.progress.qualified}·{row.progress.won}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => saveObjective(row.key, row.agencyId)} disabled={savingObj === row.key} data-testid={`obj-save-${row.key}`}>
                          {savingObj === row.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Card className="order-2 -mt-6 rounded-t-none p-4">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-md border border-white/10 p-0.5" data-testid="prospect-view-toggle">
            <button onClick={() => setView("kanban")} data-testid="view-kanban-btn"
              className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${view === "kanban" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Kanban</button>
            <button onClick={() => setView("table")} data-testid="view-table-btn"
              className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${view === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Tabella</button>
          </div>
          <div className="inline-flex rounded-md border border-white/10 p-0.5" data-testid="prospect-presidio-tabs">
            {[["attivo", "Attivi"], ["panchina", "Panchina"], ["cimitero", "Cimitero"]].map(([v, l]) => (
              <button key={v} onClick={() => setPresidioView(v)} data-testid={`presidio-tab-${v}`}
                className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${presidioView === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}{v !== "attivo" ? ` (${presidioCounts[v] ?? 0})` : ""}</button>
            ))}
          </div>
          {view === "table" && (
            <div className="flex items-center gap-2">
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome…" className="h-9 w-[200px]" data-testid="prospect-search" />
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="h-9 w-[200px]" data-testid="stage-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stage</SelectItem>
                  {STAGES.map((s) => <SelectItem key={s} value={s}>{STAGE_LABEL[s]}</SelectItem>)}
                </SelectContent>
              </Select>
              {canManage && (
                <label className="ml-1 flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground" data-testid="show-archived-toggle">
                  <Checkbox checked={showArchived} onCheckedChange={(v) => setShowArchived(!!v)} data-testid="show-archived-checkbox" />
                  Mostra archiviati
                </label>
              )}
            </div>
          )}
          <span className="text-sm text-muted-foreground">{view === "kanban" ? boardItems.length : filtered.length} prospect{(view === "table" ? filtered.length > RENDER_CAP : boardItems.length > 0) ? ` · mostrati i primi ${RENDER_CAP}${view === "kanban" ? " per colonna" : ""} — usa i filtri per restringere` : ""}</span>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="funnel-filters">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={regionFilter} onValueChange={(v) => { setRegionFilter(v); setProvinceFilter("all"); }}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="funnel-region-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le regioni</SelectItem>
              {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={provinceFilter} onValueChange={setProvinceFilter}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="funnel-province-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le province</SelectItem>
              {provinceOptions.map((pr) => <SelectItem key={pr.key} value={pr.key}>{pr.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={agencyFilter} onValueChange={setAgencyFilter}>
            <SelectTrigger className="h-9 w-[210px]" data-testid="funnel-agency-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le agenzie</SelectItem>
              <SelectItem value="none">Senza agenzia (direzionali)</SelectItem>
              {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {user?.role === "admin" && (
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="funnel-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le fonti</SelectItem>
              {sourceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          )}
          {(regionFilter !== "all" || provinceFilter !== "all" || agencyFilter !== "all" || sourceFilter !== "all") && <Button variant="outline" size="sm" onClick={() => { setRegionFilter("all"); setProvinceFilter("all"); setAgencyFilter("all"); setSourceFilter("all"); }} data-testid="funnel-clear-filters">Azzera filtri ✕</Button>}
        </div>

        <div className="mb-4 rounded-lg border border-primary/25 bg-primary/[0.06] p-3.5" data-testid="funnel-info-note">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-primary">
            <Info className="h-4 w-4" /> Come funziona il funnel
          </div>
          <p className="mb-2.5 text-xs text-muted-foreground">
            Gli stadi si aggiornano <b className="text-foreground">automaticamente</b> dalle attività registrate: non serve trascinare le schede. L'unico spostamento manuale possibile è verso <b className="text-foreground">Perso</b>.
          </p>
          <ul className="grid gap-1.5 text-xs text-muted-foreground sm:grid-cols-2">
            <li><span className="font-semibold text-slate-300">Non contattato</span> — nessuna attività registrata</li>
            <li><span className="font-semibold text-sky-300">Primo contatto</span> — telefonata, email, messaggio o videocall</li>
            <li><span className="font-semibold text-violet-300">Parlato col decision maker</span> — contatto con flag "decision maker"</li>
            <li><span className="font-semibold text-amber-300">Incontro presso cliente/azienda</span> — registrata una visita</li>
            <li><span className="font-semibold text-emerald-300">Partner / Ordine</span> — assegnati alla conversione o al primo ordine</li>
            <li><span className="font-semibold text-rose-300">Perso</span> — unico stadio spostabile manualmente</li>
          </ul>
          <p className="mt-2.5 text-xs text-muted-foreground">
            Le date di <b className="text-foreground">ricontatto</b> si calcolano da sole: dopo un contatto 21→42 gg, dopo un incontro 14→28 gg.
          </p>
        </div>

        {view === "table" && canAssign && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-primary/25 bg-primary/[0.06] px-3 py-2 text-xs text-muted-foreground" data-testid="funnel-reassign-note">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
            <span><b className="text-foreground">Cambio titolarità:</b> seleziona uno o più prospect con le caselle a sinistra, poi usa il menu <b className="text-foreground">"Riassegna a…"</b> per assegnarli a un'agenzia (o lasciarli direzionali).</span>
          </div>
        )}

        {view === "table" && canAssign && selectedIds.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-primary/40 bg-primary/10 p-3" data-testid="bulk-assign-bar">
            <span className="flex items-center gap-2 text-sm font-semibold"><Users className="h-4 w-4 text-primary" />{selectedIds.size} prospect selezionati</span>
            <Select value={bulkAgency} onValueChange={setBulkAgency}>
              <SelectTrigger className="h-9 w-[240px]" data-testid="bulk-agency-select"><SelectValue placeholder="Riassegna a…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Direzionale (nessuna agenzia)</SelectItem>
                {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={bulkAssign} disabled={!bulkAgency || bulkAssigning} data-testid="bulk-assign-btn">
              {bulkAssigning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRightCircle className="mr-2 h-4 w-4" />} Riassegna
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-clear-btn">Deseleziona</Button>
          </div>
        )}

        {view === "kanban" ? (
          <div className="grid grid-flow-col auto-cols-[minmax(230px,1fr)] gap-3 overflow-x-auto pb-2" data-testid="kanban-board">
            {STAGES.map((st) => (
              <div key={st}
                onDragOver={(e) => { if (canEdit && st === "perso") e.preventDefault(); }}
                onDrop={() => { if (st !== "perso") { dragId.current = null; return; } const p = items.find((x) => x.id === dragId.current); if (p) updateStage(p, st); dragId.current = null; }}
                className="flex flex-col rounded-lg border border-white/10 bg-white/[0.03] p-2" data-testid={`kanban-col-${st}`}>
                <div className={`mb-2 flex items-center justify-between rounded-md border px-2 py-1.5 text-[11px] font-bold uppercase tracking-wide ${STAGE_STYLE[st]}`}>
                  <span>{STAGE_LABEL[st]}</span>
                  <span className="rounded-full bg-black/30 px-1.5 py-0.5 font-mono">{byStage[st].length}</span>
                </div>
                <div className="flex min-h-[60px] max-h-[60vh] flex-col gap-2 overflow-y-auto pr-1">
                  {byStage[st].slice(0, RENDER_CAP).map((p) => (
                    <div key={p.id} draggable={canEdit && !AUTO_STAGES.includes(p.stage) && p.stage !== "perso"} onDragStart={() => { dragId.current = p.id; }}
                      className={`group rounded-md border border-white/10 bg-black/40 p-2.5 text-sm transition-colors hover:border-primary/40 ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}
                      data-testid={`kanban-card-${p.id}`}>
                      <div className="flex items-start justify-between gap-1.5">
                        <button onClick={() => openEdit(p)} className="text-left font-semibold leading-tight hover:text-primary" data-testid={`kanban-open-${p.id}`}>{p.name}</button>
                        <div className="flex shrink-0 items-center gap-1">
                          <Link to={`/configuratore/${p.id}`} title="Apri nel Configuratore" data-testid={`configuratore-prospect-${p.id}`} className="text-muted-foreground transition-colors hover:text-primary" onClick={(e) => e.stopPropagation()}>
                            <Wrench className="h-3.5 w-3.5" />
                          </Link>
                          {p.score != null && <Badge variant="outline" className="text-[9px]" title="Score azienda">S {Math.round(p.score)}</Badge>}
                        </div>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                        <span>{p.city || p.region || "—"}</span>
                        {p.agency_name && <span>· {p.agency_name}</span>}
                        {p.fascia_rischio && <span className={`rounded-full border px-1.5 py-0.5 text-[9px] ${riskCls(p.fascia_rischio)}`}>{p.fascia_rischio}</span>}
                      </div>
                      {p.funnel_prospect && (p.funnel_prospect.stato === "attivo" || p.funnel_prospect.stato === "in_pausa") && (
                        <div className={`mt-1.5 inline-flex max-w-full items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${p.funnel_prospect.stato === "attivo" ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" : "border-amber-500/40 bg-amber-500/15 text-amber-300"}`}
                          data-testid={`funnel-badge-${p.id}`} title={`Funnel ${p.funnel_prospect.tipo} · ${funnelLabels[p.funnel_prospect.tipo] || ""} — ${p.funnel_prospect.stato === "attivo" ? "attivo" : "in pausa"}`}>
                          <Mail className="h-3 w-3 shrink-0" /><span className="truncate">{p.funnel_prospect.tipo} · {funnelLabels[p.funnel_prospect.tipo] || "Funnel"}{p.funnel_prospect.stato === "in_pausa" ? " (pausa)" : ""}</span>
                        </div>
                      )}
                      {p.expected_value != null && <div className="mt-1 font-mono text-xs text-primary">{eur(p.expected_value)}</div>}
                      {(() => { const di = dueInfo(p.next_action_date); return di ? (
                        <div className={`mt-1.5 inline-flex max-w-full items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${di.cls}`} data-testid={`due-badge-${p.id}`} title={p.next_action || "Prossima azione"}>
                          <Clock className="h-3 w-3 shrink-0" /><span className="truncate">{p.next_action ? `${p.next_action} · ` : ""}{di.label}</span>
                        </div>
                      ) : null; })()}
                      {p.presidio_status && p.presidio_status !== "attivo" && (
                        <div className="mt-1.5 text-[10px] text-muted-foreground" data-testid={`presidio-reason-${p.id}`}>
                          {p.presidio_status === "cimitero" ? "Cimitero" : "Panchina"}: {p.presidio_reason}
                        </div>
                      )}
                      {canEdit && (
                        <div className="mt-2 flex items-center justify-end gap-1">
                          {presidioView === "attivo" ? (
                            <>
                              {canManage && p.stage !== "vinto" && !p.converted_partner_id && (
                                <Button variant="ghost" size="icon" className="h-6 w-6" title="Converti in Partner" onClick={() => convert(p)} data-testid={`kanban-convert-${p.id}`}><Building2 className="h-3.5 w-3.5 text-green-400" /></Button>
                              )}
                              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" title="Panchina / Cimitero" onClick={() => setPresidioDlg({ open: true, target: { id: p.id, name: p.name }, mode: "move" })} data-testid={`bench-${p.id}`}>
                                <Skull className="mr-1 h-3 w-3" /> Sospendi
                              </Button>
                              {p.stage !== "perso" && !AUTO_STAGES.includes(p.stage) && (
                                <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-red-400 hover:text-red-300" onClick={() => updateStage(p, "perso")} data-testid={`mark-lost-${p.id}`}>
                                  Perso
                                </Button>
                              )}
                            </>
                          ) : (
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-primary" onClick={() => setPresidioDlg({ open: true, target: { id: p.id, name: p.name }, mode: "restore" })} data-testid={`restore-presidio-${p.id}`}>
                              Riporta attivo
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {byStage[st].length === 0 && <div className="rounded-md border border-dashed border-white/10 py-6 text-center text-xs text-muted-foreground">Nessun prospect</div>}
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div className="max-h-[65vh] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {canAssign && <TableHead className="w-10"><Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAll} data-testid="bulk-select-all" aria-label="Seleziona tutti" /></TableHead>}
                <TableHead>Prospect</TableHead>
                <TableHead>Regione</TableHead>
                {user?.role === "admin" && <TableHead>Fonte</TableHead>}
                <TableHead>Stage</TableHead>
                <TableHead className="text-right">Valore atteso</TableHead>
                <TableHead>Prossima azione</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableRows.map((p) => (
                <TableRow key={p.id} data-testid={`prospect-row-${p.id}`} data-state={selectedIds.has(p.id) ? "selected" : undefined} onClick={() => openEdit(p)} className="cursor-pointer">
                  {canAssign && <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={selectedIds.has(p.id)} onCheckedChange={() => toggleSelect(p.id)} data-testid={`bulk-select-${p.id}`} aria-label={`Seleziona ${p.name}`} /></TableCell>}
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold hover:text-primary">{p.name}</span>
                      <Link to={`/configuratore/${p.id}`} onClick={(e) => e.stopPropagation()} title="Apri nel Configuratore" data-testid={`configuratore-prospect-row-${p.id}`} className="text-muted-foreground transition-colors hover:text-primary">
                        <Wrench className="h-3.5 w-3.5" />
                      </Link>
                      {p.score != null && <Badge variant="outline" className="text-[10px]" title="Score azienda">S {Math.round(p.score)}</Badge>}
                      {p.fascia_rischio && <span className={`rounded-full border px-1.5 py-0.5 text-[9px] ${riskCls(p.fascia_rischio)}`} title="Fascia di rischio">{p.fascia_rischio}</span>}
                      {p.archived && <Badge variant="outline" className="border-amber-500/40 text-[9px] text-amber-300" title="Archiviato automaticamente (direzionale senza dati utili)">Archiviato</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">{p.agency_name || "—"}{p.dipendenti != null ? ` · ${p.dipendenti} dip.` : ""}{p.fatturato != null ? ` · ${eur(p.fatturato)}` : ""}</div>
                  </TableCell>
                  <TableCell>{p.region || "—"}</TableCell>
                  {user?.role === "admin" && <TableCell className="text-sm text-muted-foreground">{p.source || "—"}</TableCell>}
                  <TableCell><Badge className={`border text-[11px] ${STAGE_STYLE[p.stage]}`}>{STAGE_LABEL[p.stage]}</Badge></TableCell>
                  <TableCell className="text-right font-mono">{p.expected_value != null ? eur(p.expected_value) : "—"}</TableCell>
                  <TableCell className="text-xs">{p.next_action || "—"}{p.next_action_date ? ` · ${p.next_action_date}` : ""}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {canEdit ? (
                    <div className="flex justify-end gap-1">
                      {p.archived && (
                        <Button variant="ghost" size="icon" title="Ripristina" onClick={() => restore(p)} data-testid={`restore-prospect-${p.id}`}><ArchiveRestore className="h-4 w-4 text-amber-500" /></Button>
                      )}
                      {canManage && p.stage !== "vinto" && !p.converted_partner_id && (
                        <Button variant="ghost" size="icon" title="Converti in Partner" onClick={() => convert(p)} data-testid={`convert-${p.id}`}><ArrowRightCircle className="h-4 w-4 text-green-600" /></Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-prospect-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      {presidioView === "attivo" ? (
                        <Button variant="ghost" size="icon" title="Panchina / Cimitero" onClick={() => setPresidioDlg({ open: true, target: { id: p.id, name: p.name }, mode: "move" })} data-testid={`bench-row-${p.id}`}><Skull className="h-4 w-4 text-amber-500" /></Button>
                      ) : (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-primary" onClick={() => setPresidioDlg({ open: true, target: { id: p.id, name: p.name }, mode: "restore" })} data-testid={`restore-presidio-row-${p.id}`}>Riporta attivo</Button>
                      )}
                      {canManage && <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`delete-prospect-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                    </div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={canAssign ? 8 : 7} className="py-10 text-center text-muted-foreground">Nessun prospect.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        )}
      </Card>
      </>)}

      <PresidioDialog open={presidioDlg.open} onOpenChange={(o) => setPresidioDlg((s) => ({ ...s, open: o }))} kind="prospects" target={presidioDlg.target} mode={presidioDlg.mode} onDone={load} />

      <ProspectFormDialog open={formDlg} onOpenChange={setFormDlg} prospectId={null} onSaved={load} />

      <QuickAdd hideTriggers activityOpen={pvAtt} onActivityOpenChange={setPvAtt} prospectId={editing?.id} prospectName={editing?.name} show={["activity"]} onChanged={load} />
      <VisitaB2BDialog open={pvVisita} onOpenChange={setPvVisita} partners={[]} prospects={items} defaultProspectId={editing?.id} onSaved={load} />

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="prospect-dialog">
          <DialogHeader>
            <div className="flex flex-wrap items-center justify-between gap-2 pr-6">
              <DialogTitle>{editing ? "Modifica Prospect" : "Nuovo Prospect"}</DialogTitle>
              {editing && (
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => setPvAtt(true)} data-testid="prospect-nuova-attivita-btn"><Plus className="mr-2 h-4 w-4" /> Nuova attività</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setPvVisita(true)} data-testid="prospect-registra-visita-btn"><ClipboardCheck className="mr-2 h-4 w-4" /> Registra visita</Button>
                </div>
              )}
            </div>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2 rounded-lg border-2 border-primary/40 p-3" data-testid="cliente-finale-box">
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={form.categoria === "cliente_finale"} onChange={(e) => setV("categoria", e.target.checked ? "cliente_finale" : "")} data-testid="pf-cliente-finale-toggle" />
              Prospect cliente finale
              <span className="text-[11px] font-normal text-muted-foreground">(escluso dalla mappa, categoria dedicata)</span>
            </label>
            {form.categoria === "cliente_finale" && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Tipo</Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={form.tipo_cliente === "azienda" ? "default" : "outline"} onClick={() => setV("tipo_cliente", "azienda")} data-testid="pf-tipo-azienda">Azienda</Button>
                    <Button type="button" size="sm" variant={form.tipo_cliente === "privato" ? "default" : "outline"} onClick={() => setV("tipo_cliente", "privato")} data-testid="pf-tipo-privato">Privato</Button>
                  </div>
                </div>
                {form.tipo_cliente === "azienda" && (
                  <FlagPicker key={editing?.id || "new"} options={SETTORI} label="Settore di attività" testid="pf-settore" value={form.settore} onChange={(v) => setV("settore", v)} />
                )}
                <FlagPicker key={`${editing?.id || "new"}-fonte`} options={FONTI} label="Da dove arriva il contatto" testid="pf-fonte" value={form.fonte_contatto} onChange={(v) => setV("fonte_contatto", v)} />
                <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Note di chi inserisce</Label><Textarea value={form.note_inseritore} onChange={set("note_inseritore")} data-testid="pf-note-inseritore" /></div>
              </div>
            )}
          </div>
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">{form.categoria === "cliente_finale" && form.tipo_cliente === "privato" ? "Nome e cognome" : "Ragione sociale"}</Label><Input value={form.name} onChange={set("name")} data-testid="pf-name" /></div>
            {editing && (editing.provenienza || editing.assign_method || editing.province) && (
              <div className="sm:col-span-2 flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 p-2 text-xs" data-testid="prospect-provenance">
                <span className="font-semibold uppercase tracking-wide text-muted-foreground">Provenienza:</span>
                {editing.provenienza && <Badge variant="outline">{editing.provenienza}</Badge>}
                {editing.province && <Badge variant="secondary">{editing.province}{editing.region ? ` · ${editing.region}` : ""}</Badge>}
                {editing.assign_method && <Badge className="bg-primary/15 text-foreground border-primary/30">{ASSIGN_LABEL[editing.assign_method] || editing.assign_method}</Badge>}
              </div>
            )}
            {editing && (form.score != null || form.fatturato != null || form.dipendenti != null || form.fascia_rischio || form.partita_iva) && (
              <div className="sm:col-span-2 rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="prospect-enrichment">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Dati azienda (import) · priorità di contatto</div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div><div className="text-[10px] uppercase text-muted-foreground">Score</div><div className="font-heading text-lg font-black">{form.score != null && form.score !== "" ? Math.round(form.score) : "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-muted-foreground">Rischio</div>{form.fascia_rischio ? <Badge className={`text-[10px] ${riskCls(form.fascia_rischio)}`}>{form.fascia_rischio}</Badge> : <span className="text-sm">—</span>}</div>
                  <div><div className="text-[10px] uppercase text-muted-foreground">Dipendenti</div><div className="font-heading text-lg font-black">{form.dipendenti ?? "—"}</div></div>
                  <div><div className="text-[10px] uppercase text-muted-foreground">Vendite</div><div className="font-heading text-sm font-black">{form.fatturato != null && form.fatturato !== "" ? eur(form.fatturato) : "—"}</div></div>
                </div>
                {(form.partita_iva || form.ateco || form.website) && (
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                    {form.partita_iva && <span>P.IVA: {form.partita_iva}</span>}
                    {form.ateco && <span>ATECO: {form.ateco}</span>}
                    {form.website && <a href={form.website.startsWith("http") ? form.website : `http://${form.website}`} target="_blank" rel="noreferrer" className="text-primary underline">{form.website}</a>}
                  </div>
                )}
              </div>
            )}
            <div className="space-y-1.5"><Label className="text-xs">Telefono</Label><Input value={form.phone} onChange={set("phone")} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={form.email} onChange={set("email")} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Città</Label><Input value={form.city} onChange={set("city")} /></div>
            <div className="space-y-1.5">
              <Label className="text-xs">Regione</Label>
              <Select value={form.region || undefined} onValueChange={set("region")}>
                <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {user?.role === "admin" && <div className="space-y-1.5"><Label className="text-xs">Fonte <span className="text-[10px] text-muted-foreground">(solo admin)</span></Label><Input value={form.source} onChange={set("source")} placeholder="Fiera, sito web…" data-testid="prospect-source-input" /></div>}
            <div className="space-y-1.5">
              <Label className="text-xs">Stage</Label>
              {form.converted ? (
                <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-muted-foreground" data-testid="pf-stage-auto">Automatico (Partner / Ordine): si aggiorna da solo in base a conversione e ordini.</div>
              ) : (
                <Select value={form.stage} onValueChange={set("stage")}>
                  <SelectTrigger data-testid="pf-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.filter((s) => !AUTO_STAGES.includes(s)).map((s) => <SelectItem key={s} value={s}>{STAGE_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            {isAdmin && (
              <div className="space-y-1.5">
                <Label className="text-xs">Agenzia</Label>
                <Select value={form.agency_id} onValueChange={set("agency_id")}>
                  <SelectTrigger data-testid="pf-agency"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Nessuna</SelectItem>{agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5"><Label className="text-xs">Potenziale annuo (€)</Label><Input type="number" value={form.potenziale_annuo} onChange={set("potenziale_annuo")} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Valore atteso (€)</Label><Input type="number" value={form.expected_value} onChange={set("expected_value")} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Prossima azione</Label><Input value={form.next_action} onChange={set("next_action")} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Data prossima azione</Label><Input type="date" value={form.next_action_date} onChange={set("next_action_date")} /></div>
            {form.stage === "perso" && (
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Motivo perdita</Label><Input value={form.lost_reason} onChange={set("lost_reason")} /></div>
            )}
            <div className="space-y-1.5"><Label className="text-xs">Indirizzo</Label><Input value={form.address || ""} onChange={set("address")} data-testid="pf-address" /></div>
            <div className="space-y-1.5"><Label className="text-xs">CAP</Label><Input value={form.cap || ""} onChange={set("cap")} data-testid="pf-cap" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Partita IVA</Label><Input value={form.partita_iva || ""} onChange={set("partita_iva")} data-testid="pf-piva" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Codice Fiscale</Label><Input value={form.codice_fiscale || ""} onChange={set("codice_fiscale")} data-testid="pf-cf" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Forma giuridica</Label><Input value={form.forma_giuridica || ""} onChange={set("forma_giuridica")} data-testid="pf-fg" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Codice ATECO</Label><Input value={form.ateco || ""} onChange={set("ateco")} data-testid="pf-ateco" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Data costituzione</Label><Input type="date" value={form.data_costituzione || ""} onChange={set("data_costituzione")} data-testid="pf-datacost" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Capitale sociale (€)</Label><Input type="number" value={form.capitale_sociale ?? ""} onChange={set("capitale_sociale")} data-testid="pf-capsoc" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Codice SDI</Label><Input value={form.codice_sdi || ""} onChange={set("codice_sdi")} data-testid="pf-sdi" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Sito web</Label><Input value={form.website || ""} onChange={set("website")} data-testid="pf-website" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Note</Label><Textarea value={form.notes} onChange={set("notes")} /></div>
            {editing && canManage && (
              <div className="space-y-2 rounded-lg border border-primary/25 bg-primary/[0.05] p-3 sm:col-span-2" data-testid="prospect-preaffido">
                <div className="text-sm font-semibold text-primary">Pre-affido (fido preliminare)</div>
                <p className="text-[11px] text-muted-foreground">Fido indicativo prima della conversione in cliente. Comparirà nella pagina Affidamenti con badge «Prospect».</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div className="space-y-1.5"><Label className="text-xs">Fido preliminare (€)</Label><Input type="number" value={form.affidamento_max} onChange={set("affidamento_max")} data-testid="preaffido-max" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Nota</Label><Input value={form.affidamento_note} onChange={set("affidamento_note")} data-testid="preaffido-note" /></div>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={savePreAffido} disabled={savingAffido} data-testid="preaffido-save">{savingAffido ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Salva pre-affido</Button>
                <Button type="button" size="sm" onClick={convertiCliente} disabled={convBusy} className="ml-2" data-testid="prospect-convert-btn">{convBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Converti in Cliente</Button>
              </div>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Referenti e contatti</Label>
              <ReferentiEditor value={form.referenti || []} onChange={(v) => setForm((f) => ({ ...f, referenti: v }))} testid="prospect-referenti-editor" />
            </div>
          </div>
          {editing && (
            <div className="mt-4 space-y-4">
              <QuotesForClient prospectId={editing.id} testid="prospect-crm-quotes" />
              <ActivityLog url={`/prospects/${editing.id}/activities`} title="Attività svolte sul prospect" testid="prospect-activity-log" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="save-prospect-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
